/**
 * Helpers puros (sem dependencias de Vue) especificos do cadastro de
 * Descontos: mapas de campos de restricao, dimensoes e alinhamento dos valores
 * gravados no desconto.
 *
 * A mecanica generica de combo de codigo (normalizacao, valor legado, filtro de
 * busca) vive em `@/helpers/opcoesCombo`, compartilhada com as outras telas que
 * tem campos de codigo do ERP. Ela e reexportada aqui por conveniencia de quem
 * ja importa deste modulo.
 */

import {
  alinhaValorComOpcoes,
  descricaoDe,
  filtraOpcoes,
  isVazio,
  labelOpcao,
  mesmoValor,
  normalizaTexto,
  normalizeOpcoes,
  opcoesComValorAtual,
  pareceCodigo,
  resolveValorDigitado,
  temOpcoes,
} from '@/helpers/opcoesCombo';

export {
  alinhaValorComOpcoes,
  descricaoDe,
  filtraOpcoes,
  isVazio,
  labelOpcao,
  mesmoValor,
  normalizaTexto,
  normalizeOpcoes,
  opcoesComValorAtual,
  pareceCodigo,
  resolveValorDigitado,
  temOpcoes,
};

/** Quantidade de colunas de dimensao suportadas pelo cadastro de descontos. */
export const TOTAL_DIMENSOES = 10;

/**
 * Mapa dos campos de restricao de cliente que sao alimentados pelo endpoint
 * `/admin/descontos-opcoes-restricoes`.
 *
 * `codigoLivre` marca os campos cujo id e textual no ERP — praca e rede nao tem
 * cadastro proprio e a API usa o proprio valor como descricao, entao o codigo
 * gravado pode conter espaco e acento (`ZONA SUL`). Nesses campos qualquer texto
 * digitado vale como codigo; sem isso, um valor legado que nao esteja mais na
 * lista (que so traz praca/rede de clientes *ativos*) nao poderia ser
 * redigitado — o blur descartaria em silencio.
 * @type {Array<{campo: string, chave: string, label: string, codigoLivre?: boolean}>}
 */
export const CAMPOS_RESTRICAO_CLIENTE = [
  { campo: 'filial_id', chave: 'filiais', label: 'Cód. Filial' },
  { campo: 'regiao_id', chave: 'regioes', label: 'Cód. Região' },
  { campo: 'praca_id', chave: 'pracas', label: 'Cód. Praça', codigoLivre: true },
  { campo: 'rede_id', chave: 'redes', label: 'Cód. Rede', codigoLivre: true },
  { campo: 'atividade_id', chave: 'atividades', label: 'Cód. Atividade' },
  { campo: 'supervisor_id', chave: 'supervisores', label: 'Cód. Superv.' },
  { campo: 'representante_id', chave: 'representantes', label: 'Cód. RCA' },
  { campo: 'operador_id', chave: 'operadores', label: 'Cód. Operador' },
];

/**
 * Demais campos do desconto alimentados pelo mesmo endpoint: plano de
 * pagamento e tabela de preco (aba Restricoes · Geral) e fornecedor (aba
 * Restricoes · Produto).
 * @type {Array<{campo: string, chave: string, label: string}>}
 */
export const CAMPOS_RESTRICAO_EXTRA = [
  { campo: 'planopagamento_id', chave: 'planos', label: 'Cód. Plano' },
  { campo: 'tabelapreco', chave: 'tabelasprecos', label: 'Tabela de Preço' },
  // fornecedor tambem nao tem cadastro proprio: id textual, ver CAMPOS_RESTRICAO_CLIENTE
  { campo: 'fornecedor_id', chave: 'fornecedores', label: 'Cód. Fornecedor', codigoLivre: true },
];

/**
 * Todos os campos alinhaveis pelo endpoint de opcoes de restricao.
 * @type {Array<{campo: string, chave: string, label: string}>}
 */
export const CAMPOS_RESTRICAO_TODOS = CAMPOS_RESTRICAO_CLIENTE.concat(CAMPOS_RESTRICAO_EXTRA);

/**
 * Le uma lista de opcoes do objeto devolvido por
 * `/admin/descontos-opcoes-restricoes` de forma segura.
 * @param {Object} opcoesRestricoes
 * @param {string} chave
 * @returns {Array<{id: *, descricao: string}>}
 */
export function opcoesDe(opcoesRestricoes, chave) {
  if (!opcoesRestricoes || typeof opcoesRestricoes !== 'object') return [];
  return normalizeOpcoes(opcoesRestricoes[chave]);
}

/**
 * Filtra e normaliza o array de dimensoes de `/admin/descontos-dimensoes`,
 * mantendo apenas colunas validas (1..TOTAL_DIMENSOES) que possuam opcoes.
 *
 * O endpoint pode devolver mais de uma entrada para a mesma coluna (quando o
 * ERP usa rotulos diferentes na mesma coluna — ex.: DEPARTAMENTO e SEÇÂO na
 * coluna 1 de um cliente real); como a relacao de itens e por coluna, as duas
 * entradas trazem a mesma lista e a primeira e mantida.
 * @param {Array<{coluna: *, label?: *, dimensoes?: Array}>} dimensoes
 * @returns {Array<{coluna: number, label: string, dimensoes: Array<{id: *, desc: string}>}>}
 */
export function dimensoesDisponiveis(dimensoes) {
  if (!Array.isArray(dimensoes)) return [];

  return dimensoes
    .reduce((lista, dimensao) => {
      if (!dimensao || typeof dimensao !== 'object') return lista;

      const coluna = parseInt(dimensao.coluna, 10);
      if (!coluna || coluna < 1 || coluna > TOTAL_DIMENSOES) return lista;
      if (lista.some((item) => item.coluna === coluna)) return lista;

      const opcoes = normalizeOpcoes(dimensao.dimensoes);
      if (!opcoes.length) return lista;

      lista.push({
        coluna,
        label: isVazio(dimensao.label) ? `Cód. Dim. ${coluna}` : String(dimensao.label),
        dimensoes: opcoes.map((opcao) => ({ id: opcao.id, desc: opcao.descricao })),
      });

      return lista;
    }, [])
    .sort((a, b) => a.coluna - b.coluna);
}

/**
 * Colunas de dimensao que a tela deve renderizar: as que tem cadastro na base
 * mais as que o desconto em edicao ja usa.
 *
 * Sem a segunda parte, um desconto legado (ou sincronizado do ERP) gravado numa
 * coluna sem cadastro ficaria sem campo na tela: o valor continuaria no
 * registro, valendo no motor de calculo, mas invisivel e impossivel de limpar.
 * As colunas sem cadastro entram com lista vazia — o combo se comporta como
 * input de texto livre e mostra a tag "codigo atual".
 *
 * Quando nao ha cadastro nenhum devolve vazio, para a tela cair no fallback
 * completo dos 10 campos de texto.
 * @param {Array} dimensoes array bruto de `/admin/descontos-dimensoes`
 * @param {Object} form
 * @returns {Array<{coluna: number, label: string, dimensoes: Array}>}
 */
export function dimensoesComValoresDoForm(dimensoes, form) {
  const disponiveis = dimensoesDisponiveis(dimensoes);

  if (!disponiveis.length) return [];
  if (!form || typeof form !== 'object') return disponiveis;

  const legadas = [];

  for (let coluna = 1; coluna <= TOTAL_DIMENSOES; coluna += 1) {
    const jaVisivel = disponiveis.some((item) => item.coluna === coluna);

    if (!jaVisivel && !isVazio(form[`dimensao${coluna}`])) {
      legadas.push({ coluna, label: `Cód. Dim. ${coluna}`, dimensoes: [] });
    }
  }

  return disponiveis.concat(legadas).sort((a, b) => a.coluna - b.coluna);
}

/**
 * Opcoes de uma dimensao preservando valor legado gravado no desconto.
 * @param {{dimensoes?: Array}} dimensao item ja normalizado por dimensoesDisponiveis
 * @param {*} valorAtual
 * @returns {Array<{id: *, desc: string, legado?: boolean}>}
 */
export function opcoesDimensaoComValorAtual(dimensao, valorAtual) {
  const opcoes = dimensao && Array.isArray(dimensao.dimensoes) ? dimensao.dimensoes : [];

  return opcoesComValorAtual(opcoes, valorAtual).map((opcao) => ({
    id: opcao.id,
    desc: opcao.descricao,
    legado: !!opcao.legado,
  }));
}

/**
 * Monta o patch (objeto novo) com os valores de restricao de cliente alinhados
 * aos ids devolvidos pela API. Nao muta o form recebido.
 * @param {Object} form
 * @param {Object} opcoesRestricoes
 * @param {Array<{campo: string, chave: string}>} [campos]
 * @returns {Object} apenas os campos cujo valor precisou ser ajustado
 */
export function patchValoresRestricoes(form, opcoesRestricoes, campos = CAMPOS_RESTRICAO_CLIENTE) {
  if (!form || typeof form !== 'object') return {};

  return (Array.isArray(campos) ? campos : []).reduce((patch, { campo, chave }) => {
    const alinhado = alinhaValorComOpcoes(form[campo], opcoesDe(opcoesRestricoes, chave));
    if (alinhado !== form[campo]) patch[campo] = alinhado;
    return patch;
  }, {});
}

/**
 * Monta o patch com os valores de `dimensaoN` alinhados aos ids das dimensoes.
 * Nao muta o form recebido.
 * @param {Object} form
 * @param {Array} dimensoes array bruto ou normalizado de dimensoes
 * @returns {Object} apenas os campos cujo valor precisou ser ajustado
 */
export function patchValoresDimensoes(form, dimensoes) {
  if (!form || typeof form !== 'object') return {};

  return dimensoesDisponiveis(dimensoes).reduce((patch, dimensao) => {
    const campo = `dimensao${dimensao.coluna}`;
    const alinhado = alinhaValorComOpcoes(form[campo], dimensao.dimensoes);
    if (alinhado !== form[campo]) patch[campo] = alinhado;
    return patch;
  }, {});
}

/**
 * Converte a pagina de clientes de `/admin/descontos-clientes` (`json.itens`)
 * em opcoes `{ id, descricao }` do combo, usando razao social (fallback nome
 * fantasia e descricao; sem nome algum, o proprio codigo).
 * @param {Array<{id: *, razSoc?: string, fant?: string, descricao?: string}>} itens
 * @returns {Array<{id: *, descricao: string}>}
 */
export function opcoesClientes(itens) {
  if (!Array.isArray(itens)) return [];

  return itens
    .filter((cliente) => cliente && typeof cliente === 'object' && !isVazio(cliente.id))
    .map((cliente) => {
      const nome = [cliente.razSoc, cliente.fant, cliente.descricao].find(
        (valor) => !isVazio(valor)
      );

      return { id: cliente.id, descricao: isVazio(nome) ? String(cliente.id) : String(nome) };
    });
}
